export type MissionGoAction = "A" | "B" | "C" | "D";

const actionMeta: Record<MissionGoAction, { label: string }> = {
  A: { label: "調査" },
  B: { label: "案作成" },
  C: { label: "実行準備" },
  D: { label: "保留" },
};

export interface ExecutionCardPreview {
  title: string;
  goAction: MissionGoAction | null;
  goLabel: string;
  recommendedAgent: string;
  mode: string;
}

interface MissionLikeTask {
  title: string;
  completed?: boolean;
  deleted?: boolean;
  dueDate?: string | null;
}

export function cleanAgentOutput(output: string): string {
  return output.replace(/\*\*/g, "").trim();
}

function sourceForParsing(output: string): string {
  const cleaned = cleanAgentOutput(output);
  const marker = "元のAgent Result:";
  const markerIndex = cleaned.lastIndexOf(marker);
  if (markerIndex >= 0) return cleaned.slice(markerIndex + marker.length).trim();
  return cleaned;
}

function normalizeLine(line: string): string {
  return line.replace(/^[-`*#\s]+/, "").replace(/[`*_]+/g, "").trim();
}

function outputLines(output: string): string[] {
  return sourceForParsing(output).split("\n").map(normalizeLine).filter(Boolean);
}

function isHeading(line: string): boolean {
  return /^(Primary Mission|今日の勝ち筋|Mission案|狙い|理由|根拠|今日の勝ち条件|A\/B\/C\/D|おすすめ|ネクストアクション)$/i.test(
    line
  );
}

function cleanTitleCandidate(line: string): string {
  return line
    .replace(/^\d+[.．]\s*/, "")
    .replace(/^Mission[:：]\s*/, "")
    .replace(/^目的[:：]\s*/, "")
    .replace(/^推奨[:：]\s*/, "")
    .replace(/^おすすめ[:：]\s*/, "")
    .replace(/^Codexに「(.+)」を出させる$/, "$1を作る")
    .replace(/^`?[A-D]`?\s*で[、,\s]*/, "")
    .replace(/^Codexに「(.+)」を出させる$/, "$1を作る")
    .replace(/^[A-D][.．:]?\s*/, "")
    .trim();
}

export function isWeakMissionTitle(line: string | undefined): boolean {
  if (!line) return true;
  const raw = line.trim();
  if (/^(Primary Mission|Mission|今日の勝ち筋|Agent Resultから作成したMission)$/i.test(raw)) return true;
  const cleaned = cleanTitleCandidate(line);
  return /^(Primary Mission|Mission|今日の勝ち筋|Agent Resultから作成したMission|おすすめ[:：]?\s*[A-D]?|推奨[:：]?\s*[A-D]?|[A-D])$/i.test(
    cleaned
  );
}

export function shouldRepairStoredMission(title: string, description: string): boolean {
  if (!/^実行カード/.test(description)) return false;
  if (!description.includes("元のAgent Result:")) return false;
  if (isWeakMissionTitle(title)) return true;
  return /^(おすすめ|推奨)[:：]/.test(title.trim());
}

function missionTokens(title: string): Set<string> {
  const knownTokens = [
    "TimereX",
    "予約導線",
    "focus-form",
    "診断LP",
    "本番送信",
    "diff",
    "差分案",
    "チェックリスト",
    "判断",
    "テンプレ",
    "URL",
    "LP",
    "送信者名",
    "確認",
  ];
  return new Set(knownTokens.filter((token) => title.includes(token)));
}

export function findSimilarMissionTitle(
  title: string,
  tasks: MissionLikeTask[],
  today: string
): { title: string; exact: boolean } | null {
  const normalizedTitle = title.replace(/\s/g, "").toLowerCase();
  const tokens = missionTokens(title);

  for (const task of tasks) {
    if (task.completed || task.deleted) continue;
    if (task.dueDate && task.dueDate !== today) continue;
    const normalizedExisting = task.title.replace(/\s/g, "").toLowerCase();
    if (normalizedExisting === normalizedTitle) return { title: task.title, exact: true };

    const existingTokens = missionTokens(task.title);
    const commonCount = [...tokens].filter((token) => existingTokens.has(token)).length;
    if (commonCount >= 2) return { title: task.title, exact: false };
  }

  return null;
}

function keywordTitle(lines: string[]): string | undefined {
  const source = lines.join(" ");
  if (/TimereX|予約導線|focus-form|診断LP/.test(source)) {
    if (/diff|差分案/.test(source)) return "TimereX予約導線の現状確認とdiff案を作る";
    if (/チェックリスト/.test(source)) return "TimereX予約導線の本番投入前チェックリストを作る";
    return "TimereX予約導線の本番投入判断を固める";
  }
  if (/LP|CVR|フォーム営業/.test(source)) return "獲得導線の確認と改善案を作る";
  return undefined;
}

export function missionTitleFromOutput(output: string): string {
  const lines = outputLines(output);
  const primaryMissionIndex = lines.findIndex((line) =>
    /^(Primary Mission|今日の勝ち筋|Mission案)$/i.test(line)
  );
  const primaryMission =
    primaryMissionIndex >= 0
      ? lines
          .slice(primaryMissionIndex + 1, primaryMissionIndex + 8)
          .find((line) => !isHeading(line) && !/^(A|B|C|D)[.．]/.test(line))
      : undefined;
  const nextActionIndex = lines.findIndex((line) => /^(ネクストアクション|次の1手|次にやること)/.test(line));
  const numberedNextAction =
    nextActionIndex >= 0
      ? lines
          .slice(nextActionIndex + 1, nextActionIndex + 5)
          .find((line) => /^\d+[.．]/.test(line))
      : undefined;
  const inlineMission = lines.find(
    (line) => /Mission[:：]|今日の勝ち筋[:：]/.test(line) && !isHeading(line)
  );
  const fallback = lines.find((line) => !isHeading(line) && !isWeakMissionTitle(line));
  const candidates = [
    primaryMission,
    inlineMission?.replace(/^.*[:：]\s*/, ""),
    numberedNextAction,
    keywordTitle(lines),
    fallback,
  ];
  const titleLine = candidates.find((line) => !isWeakMissionTitle(line));
  const cleanedTitle = cleanTitleCandidate(titleLine || "");
  if (/現状確認\s*\+\s*diff案のみ/.test(cleanedTitle) && keywordTitle(lines)) {
    return keywordTitle(lines)!.slice(0, 80);
  }
  return (cleanedTitle || keywordTitle(lines) || "Agent Resultから作成したMission").slice(0, 80);
}

export function inferGoFromOutput(output: string, selectedReply: MissionGoAction | null): MissionGoAction | null {
  if (selectedReply) return selectedReply;
  const lines = outputLines(output);
  const recommendedHeadingIndex = lines.findIndex((line) => /^(おすすめ|推奨)$/.test(line));
  if (recommendedHeadingIndex >= 0) {
    const nextLine = lines.slice(recommendedHeadingIndex + 1, recommendedHeadingIndex + 3).find(Boolean);
    const action = nextLine?.match(/^`?([A-D])`?[。．.、,\s:：]/)?.[1];
    if (action) return action as MissionGoAction;
  }

  for (const line of lines) {
    const inlineRecommended = line.match(/^(?:おすすめ|推奨)[:：]\s*`?([A-D])`?/);
    if (inlineRecommended?.[1]) return inlineRecommended[1] as MissionGoAction;
  }

  const choiceRecommended = lines
    .find((line) => /^[A-D][.．]/.test(line) && /おすすめ|推奨/.test(line))
    ?.match(/^([A-D])[.．]/)?.[1];
  if (choiceRecommended) return choiceRecommended as MissionGoAction;

  const nextActionIndex = lines.findIndex((line) => /^(ネクストアクション|次の1手|次にやること)/.test(line));
  if (nextActionIndex >= 0) {
    const firstNextAction = lines
      .slice(nextActionIndex + 1, nextActionIndex + 4)
      .find((line) => /^\d+[.．]\s*(推奨[:：]\s*)?`?[A-D]`?で/.test(line));
    const action = firstNextAction?.match(/^\d+[.．]\s*(?:推奨[:：]\s*)?`?([A-D])`?で/)?.[1];
    if (action) return action as MissionGoAction;
  }

  return null;
}

export function buildExecutionCardPreview(
  output: string,
  selectedReply: MissionGoAction | null
): ExecutionCardPreview {
  const cleaned = sourceForParsing(output);
  const isImplementation = /実装|diff|修正|コード|設定|focus-form|TIMEREX_URL|dry-run/i.test(cleaned);
  const needsHuman = /ログイン|予約ページ|Google|TimereX|カレンダー|表示名|メール|確認|目視|人間|たいがさん/i.test(
    cleaned
  );
  const goAction = inferGoFromOutput(cleaned, selectedReply);
  const recommendedAgent = isImplementation ? "Codex Desktop" : "Claude Code";
  const mode =
    needsHuman && isImplementation
      ? "AIで差分案まで進める。認証画面・予約導線の最終目視はたいがさん確認。"
      : needsHuman
        ? "AIは手順整理まで。ログイン・目視確認・対外判断はたいがさん。"
        : "AIで相談・案作成まで進められる。実行は別GO。";

  return {
    title: missionTitleFromOutput(cleaned),
    goAction,
    goLabel: goAction ? `GO ${goAction} / ${actionMeta[goAction].label}` : "GO未選択",
    recommendedAgent,
    mode,
  };
}

export function buildExecutionCard(output: string, selectedReply: MissionGoAction | null): string {
  const cleaned = sourceForParsing(output);
  const preview = buildExecutionCardPreview(cleaned, selectedReply);
  const nextVerb =
    preview.goAction === "A"
      ? "調査だけ"
      : preview.goAction === "B"
        ? "diff案・チェックリストだけ"
        : preview.goAction === "C"
          ? "実行準備まで"
          : preview.goAction === "D"
            ? "保留条件整理だけ"
            : "作業範囲の整理";
  const aiPrompt = `Flow Command CenterのMissionを実行可能な作業に落としてください。

Mission:
${preview.title}

GO:
${preview.goLabel}

制約:
- 本番送信なし
- 外部投稿なし
- git pushなし
- 認証情報を表示しない
- 危険作業はA/B/C/Dで確認

期待する出力:
1. AIでできる範囲
2. たいがさんが確認する範囲
3. 次に実行する具体手順
4. ${nextVerb}`;

  return `実行カード

目的:
${preview.title}

GO:
${preview.goLabel}

実行判断:
${preview.mode}

推奨実行面:
${preview.recommendedAgent}

次の行動:
1. まず下のプロンプトを ${preview.recommendedAgent} に投げて、作業範囲を確認する。
2. 認証画面・予約ページ・本番送信に触れる場面が出たら、たいがさん確認で止める。
3. diff案または確認結果が出たら、Command CenterでA/B/C/Dを再判断する。

AIへ投げるプロンプト:
\`\`\`
${aiPrompt}
\`\`\`

たいがさんが見るポイント:
- AIだけで完結する作業か
- ログイン/予約画面/メール表示名など人間確認が必要か
- 本番送信・外部書き込み・git pushが混ざっていないか

禁止:
- 本番フォーム送信
- 外部投稿
- git push
- 認証情報の表示

元のAgent Result:
${cleaned}`;
}
